import tenantConfig from './index'
import type { TenantColors } from './types'

function toKebab(str: string): string {
  return str.replaceAll(/([A-Z])/g, '-$1').toLowerCase()
}

function applyVarsToRoot(
  obj: Record<string, string>,
  root: HTMLElement,
  prefix = ''
): void {
  Object.entries(obj).forEach(([k, v]) => {
    root.style.setProperty(`--${prefix}${toKebab(k)}`, v)
  })
}

function applyColorVars(colors: TenantColors, root: HTMLElement): void {
  const map: Record<string, string> = {
    '--text-color': colors.textColorPrimary,
    '--text-color-primary': colors.textColorPrimary,
    '--text-color-secondary': colors.textColorSecondary,
    '--text-color-lighter': colors.textColorLighter,
    '--text-color-lighter-2': colors.textColorLighter2,
    '--text-color-lighter-3': colors.textColorLighter3,
    '--text-color-heading': colors.textColorHeading,
    '--text-color-content': colors.textColorContent,
    '--text-color-light': colors.textColorLight,
    '--text-color-helper': colors.textColorHelper,
    '--text-light-grey': colors.textLightGrey,
    '--text-dark-grey': colors.textDarkGrey,
    '--text-color-green': colors.textColorGreen,
    '--text-color-green-2': colors.textColorGreen2,
    '--text-color-red': colors.textColorRed,
    '--button-color-primary': colors.buttonColorPrimary,
    '--button-color-primary-gradient-start':
      colors.buttonColorPrimaryGradientStart,
    '--button-background-color-primary': colors.buttonBackgroundColorPrimary,
    '--button-background-color-subtle': colors.buttonBackgroundColorSubtle,
    '--button-background-color-lighter': colors.buttonBackgroundColorLighter,
    '--button-color-subtle': colors.buttonColorSubtle,
    '--button-color-primary-default': colors.buttonColorPrimaryDefault,
    '--button-border-color': colors.buttonBorderColor,
    '--background-color-white': colors.backgroundColorWhite,
    '--background-color-lighter': colors.backgroundColorLighter,
    '--background-color-lighter-200': colors.backgroundColorLighter200,
    '--background-color-darker': colors.backgroundColorDarker,
    '--background-color-darker-2': colors.backgroundColorDarker2,
    '--background-color-darker-3': colors.backgroundColorDarker3,
    '--background-color-lighter-red': colors.backgroundColorLighterRed,
    '--light-red-background-color': colors.lightRedBackgroundColor,
    '--dark-red-text-color': colors.darkRedTextColor,
    '--light-green-background-color': colors.lightGreenBackgroundColor,
    '--dark-green-text-color': colors.darkGreenTextColor,
    '--divider-color': colors.dividerColor,
    '--border-color': colors.borderColor,
    '--border-color-darker': colors.borderColorDarker,
    '--side-nav-shadow-color': colors.sideNavShadowColor,
    '--toolbar-border-bottom-color': colors.toolbarBorderBottomColor,
    '--dropdown-divider-color': colors.dropdownDividerColor
  }
  Object.entries(map).forEach(([k, v]) => root.style.setProperty(k, v))
}

function applyFavicon(): void {
  const { favicon, name } = tenantConfig
  document
    .querySelectorAll<HTMLLinkElement>('link[rel="icon"]')
    .forEach((l) => l.setAttribute('href', favicon))
  document
    .querySelectorAll<HTMLLinkElement>('link[rel="apple-touch-icon"]')
    .forEach((l) => l.setAttribute('href', favicon))

  const manifestLink = document.querySelector<HTMLLinkElement>(
    'link[rel="manifest"]'
  )
  if (manifestLink) {
    const manifest = {
      short_name: name,
      name: `${name} CRM`,
      icons: [
        { src: favicon, sizes: '192x192', type: 'image/png' },
        { src: favicon, sizes: '512x512', type: 'image/png' }
      ],
      start_url: '.',
      display: 'standalone',
      theme_color: tenantConfig.theme.emailPrimaryHex,
      background_color: '#ffffff'
    }
    const blob = new Blob([JSON.stringify(manifest)], {
      type: 'application/json'
    })
    manifestLink.setAttribute('href', URL.createObjectURL(blob))
  }
}

function applyThemeColor(): void {
  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta) meta.setAttribute('content', tenantConfig.theme.emailPrimaryHex)
}

export function applyTenantTheme(): void {
  const { theme, colors, name } = tenantConfig
  const root = document.documentElement

  applyVarsToRoot(theme as unknown as Record<string, string>, root)
  applyColorVars(colors, root)

  // Tenant interactive primary (the shared Button reads --button-background-
  // color-primary for its solid fill). Overrides the generated default.
  root.style.setProperty(
    '--button-background-color-primary',
    tenantConfig.buttonPrimaryHex
  )
  root.style.setProperty(
    '--button-background-color-primary-hover',
    tenantConfig.buttonPrimaryHoverHex
  )

  // Forms, drawers and dialogs use rsuite's <Button appearance="primary">
  // directly rather than the shared atom, and rsuite fills those from
  // --rs-btn-primary-*. Point them at the same seed so "primary" is one colour
  // app-wide. These must be set here (inline on <html>) rather than in a
  // stylesheet: rsuite declares its defaults under `:root:not(.rs-theme-dark)`,
  // which outranks a plain `:root` rule of ours.
  root.style.setProperty(
    '--rs-btn-primary-bg',
    'var(--button-background-color-primary)'
  )
  root.style.setProperty(
    '--rs-btn-primary-hover-bg',
    'var(--button-background-color-primary-hover)'
  )
  root.style.setProperty(
    '--rs-btn-primary-active-bg',
    'var(--button-background-color-primary-hover)'
  )

  // Toggles (table row activate/deactivate, form switches) default to
  // --rs-primary-500, which reads near-black against a navy seed. Put them on
  // the same primary as the buttons so every "on" state is one colour.
  root.style.setProperty(
    '--rs-toggle-checked-bg',
    'var(--button-background-color-primary)'
  )
  root.style.setProperty(
    '--rs-toggle-checked-hover-bg',
    'var(--button-background-color-primary-hover)'
  )

  // ── Form controls → the same royal-blue primary as the buttons ──────────────
  // Every rsuite form control derives its "primary" from --rs-primary-500, which
  // is the tenant's navy seed (see the scale below) — so a checked box, a focused
  // input or a picked date came out navy while the button beside it was royal
  // blue. Point the FORM tokens (and only those) at the button primary, so one
  // colour means "primary" across a form. Deliberately NOT re-pointing
  // --rs-primary-* wholesale: that same scale also drives the sidebar, tables
  // (sort/resize), pagination and steps, which must keep the navy seed.
  //
  // Set inline on <html> for the same reason as the buttons above: rsuite
  // declares these under `:root:not(.rs-theme-dark)`, which outranks a plain
  // `:root` rule in our own stylesheet.
  const FORM_PRIMARY = 'var(--button-background-color-primary)'
  const FORM_PRIMARY_HOVER = 'var(--button-background-color-primary-hover)'
  /** A translucent wash of the primary — selection tints and the focus ring. */
  const primaryTint = (pct: number) =>
    `color-mix(in srgb, ${FORM_PRIMARY} ${pct}%, transparent)`

  const FORM_CONTROL_VARS: Record<string, string> = {
    // Checkbox / radio / radio tile — the checked fill.
    '--rs-checkbox-checked-bg': FORM_PRIMARY,
    '--rs-radio-checked-bg': FORM_PRIMARY,
    '--rs-radio-tile-checked-color': FORM_PRIMARY,
    // Text/number/textarea inputs — focused border + the ring around it.
    '--rs-input-focus-border': FORM_PRIMARY,
    '--rs-color-focus-ring': primaryTint(25),
    // Select / dropdown (rsuite Picker): the chosen value, the count bubble on a
    // multi-select, and the highlighted option in the open menu.
    '--rs-picker-value': FORM_PRIMARY,
    '--rs-picker-count-bg': FORM_PRIMARY,
    '--rs-listbox-option-selected-text': FORM_PRIMARY,
    '--rs-listbox-option-hover-text': FORM_PRIMARY,
    '--rs-listbox-option-selected-bg': primaryTint(8),
    // Date picker. --rs-bg-active is the selected day's fill (only the calendar
    // reads it), --rs-calendar-today-bg the today marker, and the range tint sits
    // between the two ends of a range selection.
    '--rs-bg-active': FORM_PRIMARY,
    '--rs-calendar-today-bg': FORM_PRIMARY,
    '--rs-calendar-cell-selected-hover-bg': FORM_PRIMARY_HOVER,
    '--rs-calendar-range-bg': primaryTint(12),
    // Slider + file uploader — the remaining primary-driven form inputs.
    '--rs-slider-progress': FORM_PRIMARY,
    '--rs-slider-thumb-border': FORM_PRIMARY,
    '--rs-uploader-dnd-hover-border': FORM_PRIMARY
  }
  Object.entries(FORM_CONTROL_VARS).forEach(([k, v]) =>
    root.style.setProperty(k, v)
  )

  // RSuite primary palette ↔ tenant primary. Every stop is derived from the
  // brand HSL so the scale stays on-hue *and* on-brand for any tenant.
  //
  // Two rules this has to respect, both of which the old hardcoded stops broke
  // on a dark brand like navy:
  //   1. The light tints (100/200) damp the brand saturation. Holding it at
  //      85% turned a navy hue into cornflower blue — a colour that appears
  //      nowhere in the brand — which is what the sidebar's active row used.
  //   2. The dark stops (700/800/900) must land at or below the primary's own
  //      lightness, because RSuite uses them for hover/pressed states. Fixed
  //      48/38/28% lightness put them *above* an 11%-lightness navy, so
  //      pressing a primary button lightened it into that same blue.
  const primaryParts = theme.primary.split(/[,\s]+/).map((p) => Number.parseFloat(p))
  const primaryHue = primaryParts[0]
  const primarySat = Number.isFinite(primaryParts[1]) ? primaryParts[1] : 100
  const primaryLight = Number.isFinite(primaryParts[2]) ? primaryParts[2] : 50
  const tintSat = Math.round(primarySat * 0.6)
  const shade = (factor: number) => Math.round(primaryLight * factor)
  root.style.setProperty('--rs-primary-100', `hsl(${primaryHue}, ${tintSat}%, 95%)`)
  root.style.setProperty('--rs-primary-200', `hsl(${primaryHue}, ${tintSat}%, 88%)`)
  root.style.setProperty('--rs-primary-500', `hsl(${theme.primary})`)
  root.style.setProperty('--rs-primary-600', `hsl(${theme.primary})`)
  root.style.setProperty('--rs-primary-700', `hsl(${primaryHue}, ${primarySat}%, ${shade(0.78)}%)`)
  root.style.setProperty('--rs-primary-800', `hsl(${primaryHue}, ${primarySat}%, ${shade(0.6)}%)`)
  root.style.setProperty('--rs-primary-900', `hsl(${primaryHue}, ${primarySat}%, ${shade(0.42)}%)`)

  // Canonical brand tokens — the single hook for any component that needs the
  // brand primary/accent directly (SCSS via var(), or JS via getComputedStyle).
  // Rebranding the seed updates these automatically.
  root.style.setProperty('--brand-primary', theme.emailPrimaryHex)
  root.style.setProperty('--brand-primary-foreground', '#ffffff')
  // Interactive-primary accent (replaces the gold). White foreground so any
  // accent-filled chip/button stays legible.
  root.style.setProperty('--brand-accent', tenantConfig.buttonPrimaryHex)
  root.style.setProperty('--brand-accent-foreground', '#ffffff')
  root.style.setProperty(
    '--button-color-primary',
    tenantConfig.buttonPrimaryHex
  )

  root.style.setProperty('--header-logo-bg', `hsl(${theme.primary})`)
  root.style.setProperty('--sidenav-toggle-bg', `hsl(${theme.ring})`)
  root.style.setProperty(
    '--font-primary',
    "'Inter', -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI', sans-serif"
  )
  root.style.setProperty(
    '--background-color-brand',
    tenantConfig.authBrandBackground
  )

  document.title = `${name} CRM`

  const metaDesc = document.querySelector('meta[name="description"]')
  if (metaDesc) metaDesc.setAttribute('content', `${name} CRM`)
  const metaAuthor = document.querySelector('meta[name="author"]')
  if (metaAuthor) metaAuthor.setAttribute('content', name)

  applyFavicon()
  applyThemeColor()
}

export function applyDarkTheme(): void {
  const { darkTheme } = tenantConfig
  const root = document.documentElement
  applyVarsToRoot(darkTheme as unknown as Record<string, string>, root)
  root.style.setProperty('--rs-primary-500', `hsl(${darkTheme.primary})`)
  root.style.setProperty('--rs-primary-700', `hsl(${darkTheme.ring})`)
  root.style.setProperty('--rs-primary-900', `hsl(${darkTheme.ring})`)
  root.style.setProperty('--header-logo-bg', `hsl(${darkTheme.primary})`)
  root.style.setProperty('--sidenav-toggle-bg', `hsl(${darkTheme.ring})`)
}

export function applyLightTheme(): void {
  applyTenantTheme()
}
